import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { CreditCard, X, ChevronRight } from 'lucide-react';
import ReactDOM from 'react-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/publishable-key`);
      if (response.data.success) {
        stripePromise = loadStripe(response.data.publishable_key);
      }
    } catch (error) {
      console.error('Error loading Stripe key:', error);
    }
  }
  return stripePromise;
};

// Payment Modal Component
const PaymentModal = ({ show, onClose, amount, onPaymentSuccess, orderData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      onPaymentSuccess(paymentMethod);
    } catch (err) {
      setError('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-700">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Complete Payment</h3>
              <p className="text-sm text-gray-400 mt-1">Pay ${amount.toFixed(2)}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="text-gray-400 hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-gray-750 p-4 rounded-lg border border-gray-600">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Card Details *
              </label>
              <div className="bg-[#374a5e] border border-gray-600 rounded-lg p-3">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#fff',
                        '::placeholder': { color: '#9ca3af' },
                      },
                      invalid: { color: '#ef4444' },
                    },
                  }}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing || !stripe}
                className="flex-1 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-md hover:bg-[rgb(31,163,201)] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    <span>Pay ${amount.toFixed(2)}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

const PayAtTable = () => {
  const navigate = useNavigate();
  const { barcodeIdentifier } = useParams();
  const location = useLocation();
  const isFromBarcodeScan = location.pathname.includes('/barcode/');
  
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTableModal, setShowTableModal] = useState(!isFromBarcodeScan);  // Hide table selection if from barcode
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableOrders, setTableOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  const [selectedTipOption, setSelectedTipOption] = useState(null);
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [ecommerceSettings, setEcommerceSettings] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);

  useEffect(() => {
    fetchTables();
    fetchEcommerceSettings();
    fetchStoreSettings();
    getStripePromise(); // Initialize Stripe early
  }, []);

  // Handle barcode scan - fetch order directly
  useEffect(() => {
    if (barcodeIdentifier && isFromBarcodeScan) {
      fetchOrderFromBarcode(barcodeIdentifier);
    }
  }, [barcodeIdentifier, isFromBarcodeScan]);

  const fetchOrderFromBarcode = async (identifier) => {
    try {
      setLoading(true);
      // Fetch payment-enabled orders for this barcode
      const response = await axios.get(`${API_BASE_URL}/api/pay-at-table/barcode/${identifier}`);
      
      if (response.data.success && response.data.assigned) {
        const orders = response.data.orders || [];
        const table = response.data.table;
        
        if (orders.length === 0) {
          alert('No payment-enabled orders found for this table. Please contact staff for assistance.');
          return;
        }
        
        // Set the table and orders
        setSelectedTable(table);
        setTableOrders(orders);
        
        // Combine all orders into a single order object for display
        if (orders.length > 1) {
          // Multiple orders - combine them
          console.log(`Combining ${orders.length} orders for table ${table.name}`);
          
          const combinedOrder = {
            ...orders[0], // Use first order as base
            _isCombined: true,
            _combinedOrders: orders.map(o => o.id),
            items: orders.flatMap(o => o.items || []),
            subtotal: orders.reduce((sum, o) => sum + (o.subtotal || 0), 0),
            tax: orders.reduce((sum, o) => sum + (o.tax || 0), 0),
            total: orders.reduce((sum, o) => sum + (o.total || 0), 0),
            customer: orders[0].customer // Use first order's customer info
          };
          
          setSelectedOrder(combinedOrder);
        } else {
          // Single order - show as is
          setSelectedOrder(orders[0]);
        }
        
        setShowOrderDetailsModal(true);
        setShowTableModal(false);
      } else if (response.data.success && !response.data.assigned) {
        alert(response.data.message || 'This barcode is not assigned to a table yet. Please contact staff.');
      } else {
        alert('No order found for this barcode. Please ask your waiter to enable payment.');
      }
    } catch (error) {
      console.error('Error fetching order from barcode:', error);
      alert('Error loading order. Please try again or ask your waiter for assistance.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-info`);
      setStoreSettings(response.data);
    } catch (error) {
      console.error('Error fetching store settings:', error);
    }
  };

  const fetchEcommerceSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/ecommerce-settings`);
      setEcommerceSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchTables = async () => {
    try {
      // Fetch all orders with pending payment status (public endpoint)
      const ordersResponse = await axios.get(`${API_BASE_URL}/api/orders/public/pending-payment`);
      console.log('Orders response:', ordersResponse.data);
      
      const pendingOrders = ordersResponse.data.orders || [];
      console.log('Pending payment orders:', pendingOrders.length);
      
      // Group orders by table (using assigned_table_id and assigned_table_name)
      const tablesMap = new Map();
      const ordersWithoutTableName = [];
      
      pendingOrders.forEach(order => {
        const tableId = order.assigned_table_id;
        const tableName = order.assigned_table_name || order.table_name;
        
        if (tableId) {
          // Has table ID - group by table ID
          if (!tablesMap.has(tableId)) {
            tablesMap.set(tableId, {
              id: tableId,
              name: tableName || 'Unnamed Table',
              category: 'General',
              hasPendingPayment: true,
              orders: []
            });
          }
          tablesMap.get(tableId).orders.push(order);
        } else if (tableName) {
          // No table ID but has table name (manual entry) - group by table name
          const groupKey = `manual_${tableName}`;
          if (!tablesMap.has(groupKey)) {
            tablesMap.set(groupKey, {
              id: groupKey,
              name: `Table ${tableName}`,
              category: 'Manual Entry',
              hasPendingPayment: true,
              orders: []
            });
          }
          tablesMap.get(groupKey).orders.push(order);
        } else {
          // No table ID or name - truly counter orders
          ordersWithoutTableName.push(order);
        }
      });
      
      // Add a virtual "Counter Orders" section only for orders with NO table info at all
      if (ordersWithoutTableName.length > 0) {
        tablesMap.set('counter', {
          id: 'counter',
          name: 'Counter Orders',
          category: 'Counter',
          hasPendingPayment: true,
          orders: ordersWithoutTableName
        });
      }
      
      // Convert map to array
      const tablesWithOrders = Array.from(tablesMap.values());
      console.log('Tables with pending orders:', tablesWithOrders.length);
      console.log('Counter orders without table:', ordersWithoutTableName.length);
      
      setTables(tablesWithOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tables:', error);
      setLoading(false);
    }
  };

  const handleTableSelect = (table) => {
    console.log('Table selected:', table.name, 'Orders:', table.orders?.length);
    
    setSelectedTable(table);
    const ordersForTable = table.orders || [];
    setTableOrders(ordersForTable);
    
    if (ordersForTable.length > 0) {
      // Combine all orders into a single order for payment
      const combinedOrder = combineOrders(ordersForTable);
      setSelectedOrder(combinedOrder);
      setShowTableModal(false);
      setShowOrderDetailsModal(true);
    } else {
      alert('No pending payment orders found for this table. Please ask your waiter to enable payment.');
    }
  };

  const combineOrders = (orders) => {
    if (orders.length === 0) return null;
    if (orders.length === 1) return orders[0];

    console.log('Combining orders:', orders.length);
    
    // Combine all items from all orders
    const allItems = [];
    let totalSubtotal = 0;
    let totalTax = 0;
    let totalAmount = 0;

    orders.forEach((order, index) => {
      console.log(`Order ${index + 1}:`, {
        id: order.id,
        itemsCount: order.items?.length || 0,
        subtotal: order.subtotal,
        tax: order.tax,
        total: order.total
      });
      
      if (order.items && Array.isArray(order.items)) {
        console.log(`Adding ${order.items.length} items from order ${order.id}`);
        allItems.push(...order.items);
      }
      totalSubtotal += order.subtotal || 0;
      totalTax += order.tax || 0;
      totalAmount += order.total || 0;
    });

    console.log('Combined items count:', allItems.length);
    console.log('Combined totals:', { subtotal: totalSubtotal, tax: totalTax, total: totalAmount });

    // Create a combined order object
    const combinedOrder = {
      ...orders[0], // Use first order as base
      items: allItems,
      subtotal: totalSubtotal,
      tax: totalTax,
      total: totalAmount,
      _combinedOrders: orders.map(o => o.id), // Track all order IDs for payment
      _isCombined: true,
      _orderCount: orders.length
    };

    console.log('Final combined order:', {
      itemsCount: combinedOrder.items.length,
      subtotal: combinedOrder.subtotal,
      tax: combinedOrder.tax,
      total: combinedOrder.total,
      combinedOrderIds: combinedOrder._combinedOrders
    });

    return combinedOrder;
  };

  const handleConfirmOrder = () => {
    // Auto-populate customer name if available
    if (selectedOrder?.customer?.name && !checkoutForm.fullName) {
      setCheckoutForm(prev => ({
        ...prev,
        fullName: selectedOrder.customer.name
      }));
    }
    setShowOrderDetailsModal(false);
    setShowCheckoutModal(true);
  };

  const handleBackToTables = () => {
    setShowOrderDetailsModal(false);
    setShowCheckoutModal(false);
    setShowTableModal(true);
    setSelectedTable(null);
    setSelectedOrder(null);
  };

  const calculateTip = () => {
    if (!selectedOrder) return 0;
    
    const subtotal = selectedOrder.subtotal || 0;
    
    if (selectedTipOption === 'custom') {
      return parseFloat(customTipAmount) || 0;
    }
    
    if (selectedTipOption !== null) {
      return subtotal * (selectedTipOption / 100);
    }
    
    return 0;
  };

  const handlePaymentSuccess = async (paymentMethod) => {
    setPaymentProcessing(true);
    
    try {
      const tipAmount = calculateTip();
      const totalWithTip = selectedOrder.total + tipAmount;

      // If this is a combined order, update all orders
      if (selectedOrder._isCombined && selectedOrder._combinedOrders) {
        console.log('Processing payment for combined orders:', selectedOrder._combinedOrders);
        
        // Update all orders in parallel
        const updatePromises = selectedOrder._combinedOrders.map(orderId =>
          axios.patch(
            `${API_BASE_URL}/api/orders/public/${orderId}/complete-payment`,
            {
              paymentMethod: 'card',
              paymentMethodId: paymentMethod.id,
              tip: tipAmount / selectedOrder._combinedOrders.length, // Distribute tip equally
              total: totalWithTip, // Store total on each order for reference
              payerInfo: checkoutForm,
              _combinedPayment: true,
              _combinedTotal: totalWithTip
            }
          )
        );

        const results = await Promise.all(updatePromises);
        const allSuccessful = results.every(res => res.data.success);

        if (allSuccessful) {
          setShowPaymentModal(false);
          setShowCheckoutModal(false);
          setShowConfirmationModal(true);
        } else {
          throw new Error('Some orders failed to update');
        }
      } else {
        // Single order payment
        const response = await axios.patch(
          `${API_BASE_URL}/api/orders/public/${selectedOrder.id}/complete-payment`,
          {
            paymentMethod: 'card',
            paymentMethodId: paymentMethod.id,
            tip: tipAmount,
            total: totalWithTip,
            payerInfo: checkoutForm
          }
        );

        if (response.data.success) {
          setShowPaymentModal(false);
          setShowCheckoutModal(false);
          setShowConfirmationModal(true);
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(41,173,211)]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Table Selection Modal - White Theme */}
      {showTableModal && ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl">
                {/* Compact Header */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                  <h2 className="text-lg font-semibold text-white">Select Your Table</h2>
                  <button onClick={() => navigate('/')} className="text-white hover:opacity-80">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto flex-1">
                  {tables.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-700 text-lg mb-2">No tables are waiting for payment</p>
                      <p className="text-gray-500 text-sm">Please ask your waiter to enable payment for your table</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {tables.map((table) => {
                      const tableName = table.name || 'Unnamed Table';
                      const customerName = table.orders?.[0]?.customer?.name || '';
                      const orderCount = table.orders?.length || 0;
                      
                      return (
                        <button
                          key={table.id}
                          onClick={() => handleTableSelect(table)}
                          className="px-4 py-5 border border-gray-200 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">{tableName}</div>
                            {customerName && (
                              <div className="text-xs text-gray-600 mt-1">{customerName}</div>
                            )}
                            {orderCount > 1 && (
                              <div className="text-xs text-theme-blue mt-1 font-semibold">
                                {orderCount} orders
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Order Details Modal - NO PRICING - White Theme */}
          {showOrderDetailsModal && selectedOrder && ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-[60] p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
                {/* Compact Header with Back Button (hidden when accessed via barcode) */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                  {!isFromBarcodeScan && (
                    <button 
                      onClick={handleBackToTables} 
                      className="text-white hover:opacity-80 flex items-center gap-1"
                    >
                      <ChevronRight className="h-5 w-5 rotate-180" />
                      <span className="text-sm font-medium">Back</span>
                    </button>
                  )}
                  <h2 className={`text-lg font-semibold text-white ${isFromBarcodeScan ? '' : 'absolute left-1/2 transform -translate-x-1/2'}`}>
                    Order Details
                  </h2>
                  {!isFromBarcodeScan && <div className="w-16"></div>}
                </div>

                {/* Scrollable Content */}
                <div className="p-4 space-y-3 overflow-y-auto flex-1">
                  {/* Combined Orders Notice */}
                  {selectedOrder._isCombined && selectedOrder._combinedOrders && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                      <p className="text-xs text-blue-800">
                        <span className="font-semibold">{selectedOrder._combinedOrders.length} orders</span> combined
                      </p>
                    </div>
                  )}

                  {/* Table and Customer Info */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Table</span>
                      <span className="text-sm font-semibold text-gray-900">{selectedTable?.name || 'Unnamed Table'}</span>
                    </div>
                    {selectedOrder.customer?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">Customer</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedOrder.customer.name}</span>
                      </div>
                    )}
                  </div>

                  {/* Order Summary - Items Only */}
                  <div className="border-t border-gray-200 pt-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      Order Summary
                      <span className="text-xs text-gray-600 ml-2">({selectedOrder.items?.length || 0} items)</span>
                    </h3>
                    <div className="space-y-2">
                      {selectedOrder.items && selectedOrder.items.length > 0 ? selectedOrder.items.map((item, index) => (
                        <div key={index} className="bg-gray-100 rounded p-2.5 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm text-gray-900 font-medium">{item.quantity}x</span>
                                <span className="text-sm text-gray-900 font-medium">{item.name}</span>
                              </div>
                              {/* Variant - Single object */}
                              {item.variant && item.variant.name && (
                                <div className="mt-1 text-xs text-gray-600">
                                  • {item.variant.name}
                                </div>
                              )}
                              {/* Options/Variants - Array */}
                              {item.selectedOptions && item.selectedOptions.length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {item.selectedOptions.map((option, optIdx) => (
                                    <div key={optIdx} className="text-xs text-gray-600">
                                      • {option.name}: {option.value}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {item.options && Array.isArray(item.options) && item.options.length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {item.options.map((opt, optIdx) => {
                                    // Handle different option structures safely
                                    const optionName = opt.option_name || opt.name || (typeof opt === 'string' ? opt : 'Option');
                                    return (
                                      <div key={optIdx} className="text-xs text-gray-600">
                                        • {optionName}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {item.variants && Array.isArray(item.variants) && item.variants.length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {item.variants.map((variant, varIdx) => {
                                    // Handle different variant structures safely
                                    const variantName = typeof variant === 'string' ? variant : (variant.name || 'Variant');
                                    return (
                                      <div key={varIdx} className="text-xs text-gray-600">
                                        • {variantName}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {item.customization && (
                                <div className="mt-1 text-xs text-gray-600">
                                  Note: {item.customization}
                                </div>
                              )}
                              {item.specialInstructions && (
                                <div className="mt-1 text-xs text-gray-600">
                                  Note: {item.specialInstructions}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="text-center py-4 text-gray-500">No items found</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Compact Footer with Confirm Button */}
                <div className="px-4 py-2 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                  <button
                    onClick={handleConfirmOrder}
                    className="w-full py-2 bg-gray-200 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors hover:bg-gray-300"
                  >
                    Confirm Order
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Checkout Modal - WITH PRICING (Dark Theme) */}
          {showCheckoutModal && selectedOrder && ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-[60] p-4 overflow-y-auto">
              <div className="bg-[#2C3E50] rounded-lg max-w-2xl w-full my-8 shadow-2xl">
                {/* Compact Header */}
                <div className="px-4 py-2.5 border-b border-gray-600 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-gray-300" />
                    <h2 className="text-lg font-semibold text-white">Checkout</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-2 py-1 bg-gray-700 bg-opacity-30 rounded border border-gray-600">
                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs text-gray-400 font-medium">Pay at Table</span>
                    </div>
                    <button onClick={handleBackToTables} className="text-gray-300 hover:text-white">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto" style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#374a5e #2C3E50'
                }}>
                <style>{`
                  .p-4.space-y-4::-webkit-scrollbar {
                    width: 6px;
                  }
                  .p-4.space-y-4::-webkit-scrollbar-track {
                    background: #2C3E50;
                  }
                  .p-4.space-y-4::-webkit-scrollbar-thumb {
                    background: #374a5e;
                    border-radius: 3px;
                  }
                  .p-4.space-y-4::-webkit-scrollbar-thumb:hover {
                    background: #4a5f75;
                  }
                `}</style>
                  {/* Table Name Section */}
                  <div className="bg-gray-700 bg-opacity-30 rounded-lg p-3 border border-gray-600">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Table</span>
                      <span className="text-sm font-semibold text-white">{selectedTable?.name || 'Unnamed Table'}</span>
                    </div>
                    {selectedOrder._isCombined && selectedOrder._combinedOrders && (
                      <div className="mt-2 pt-2 border-t border-gray-600">
                        <span className="text-xs text-blue-300">
                          {selectedOrder._combinedOrders.length} orders combined
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Customer Information */}
                  <div className="bg-gray-700 bg-opacity-30 rounded-lg p-3 border border-gray-600">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="h-4 w-4 text-[rgb(41,173,211)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <h3 className="text-sm font-semibold text-white">Customer Information</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Full Name *</label>
                        <input
                          type="text"
                          name="fullName"
                          autoComplete="name"
                          value={checkoutForm.fullName}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, fullName: e.target.value })}
                          className="w-full px-3 py-2 text-sm bg-[#374a5e] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent autofill:bg-[#374a5e] autofill:text-white"
                          style={{
                            WebkitBoxShadow: '0 0 0 1000px #374a5e inset',
                            WebkitTextFillColor: 'white'
                          }}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Email *</label>
                        <input
                          type="email"
                          name="email"
                          autoComplete="email"
                          value={checkoutForm.email}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                          className="w-full px-3 py-2 text-sm bg-[#374a5e] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent autofill:bg-[#374a5e] autofill:text-white"
                          style={{
                            WebkitBoxShadow: '0 0 0 1000px #374a5e inset',
                            WebkitTextFillColor: 'white'
                          }}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          autoComplete="tel"
                          value={checkoutForm.phone}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })}
                          className="w-full px-3 py-2 text-sm bg-[#374a5e] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent autofill:bg-[#374a5e] autofill:text-white"
                          style={{
                            WebkitBoxShadow: '0 0 0 1000px #374a5e inset',
                            WebkitTextFillColor: 'white'
                          }}
                          placeholder="+1 (555) 123-4567"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tip Selection */}
                  {ecommerceSettings?.orderAtTable?.enableTip && (
                    <div className="bg-gray-700 bg-opacity-30 rounded-lg p-3 border border-gray-600">
                      <h3 className="text-xs font-semibold text-white mb-2">Add Tip (Optional)</h3>
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        {ecommerceSettings.orderAtTable.tipOptions.map((percentage) => (
                          <button
                            key={percentage}
                            type="button"
                            onClick={() => {
                              setSelectedTipOption(percentage);
                              setCustomTipAmount('');
                            }}
                            className={`py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                              selectedTipOption === percentage
                                ? 'bg-[rgb(41,173,211)] text-white'
                                : 'bg-[#374a5e] text-gray-300 hover:bg-opacity-80'
                            }`}
                          >
                            {percentage}%
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTipOption('custom');
                            setCustomTipAmount('');
                          }}
                          className={`py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                            selectedTipOption === 'custom'
                              ? 'bg-[rgb(41,173,211)] text-white'
                              : 'bg-[#374a5e] text-gray-300 hover:bg-opacity-80'
                          }`}
                        >
                          Custom
                        </button>
                      </div>
                      
                      {selectedTipOption === 'custom' && (
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={customTipAmount}
                            onChange={(e) => setCustomTipAmount(e.target.value)}
                            placeholder="Enter tip amount"
                            className="w-full pl-8 pr-3 py-2 text-sm bg-[#374a5e] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)]"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Coupon Code */}
                  <div className="bg-gray-700 bg-opacity-30 rounded-lg p-3 border border-gray-600">
                    <h3 className="text-xs font-semibold text-white mb-2">Promo Code (Optional)</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Enter promo code"
                        className="flex-1 px-3 py-2 text-sm bg-[#374a5e] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)]"
                      />
                      <button
                        type="button"
                        className="px-3 py-2 text-sm bg-[#374a5e] text-gray-300 rounded-lg hover:bg-opacity-80 font-medium border border-gray-600"
                      >
                        Apply
                      </button>
                    </div>
                  </div>

                  {/* Order Summary with Pricing */}
                  <div className="bg-gray-700 bg-opacity-30 rounded-lg p-3 border border-gray-600">
                    <h3 className="text-xs font-semibold text-white mb-3">
                      Order Summary
                      <span className="text-gray-400 ml-2">({selectedOrder.items?.length || 0} items)</span>
                    </h3>
                    <div className="space-y-2.5 mb-3">
                      {selectedOrder.items && selectedOrder.items.length > 0 ? selectedOrder.items.map((item, index) => (
                        <div key={index}>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-300">{item.quantity}x {item.name}</span>
                            <span className="text-white font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                          {/* Show variant */}
                          {item.variant && item.variant.name && (
                            <div className="flex justify-between text-xs text-gray-400 ml-4 mt-0.5">
                              <span>• {item.variant.name}</span>
                              {item.variant.price > 0 && (
                                <span>${item.variant.price.toFixed(2)}</span>
                              )}
                            </div>
                          )}
                          {/* Show selected options */}
                          {item.selectedOptions && item.selectedOptions.length > 0 && (
                            <div className="ml-4 mt-0.5">
                              {item.selectedOptions.map((option, optIdx) => (
                                <div key={optIdx} className="flex justify-between text-xs text-gray-400">
                                  <span>• {option.name}: {option.value}</span>
                                  {option.price > 0 && (
                                    <span>+${option.price.toFixed(2)}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Show options array */}
                          {item.options && Array.isArray(item.options) && item.options.length > 0 && (
                            <div className="ml-4 mt-0.5">
                              {item.options.map((opt, optIdx) => {
                                // Handle different option structures
                                const optionName = opt.option_name || opt.name || (typeof opt === 'string' ? opt : 'Option');
                                const optionPrice = opt.price || 0;
                                return (
                                  <div key={optIdx} className="flex justify-between text-xs text-gray-400">
                                    <span>• {optionName}</span>
                                    {optionPrice > 0 && (
                                      <span>+${optionPrice.toFixed(2)}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )) : (
                        <div className="text-center py-4 text-gray-400">No items found</div>
                      )}
                    </div>
                    
                    <div className="border-t border-gray-600 pt-2.5 space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Subtotal</span>
                        <span className="text-gray-300">${selectedOrder.subtotal.toFixed(2)}</span>
                      </div>
                      {selectedOrder.tax > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Tax</span>
                          <span className="text-gray-300">${selectedOrder.tax.toFixed(2)}</span>
                        </div>
                      )}
                      {calculateTip() > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Tip</span>
                          <span className="text-green-400">${calculateTip().toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-base border-t border-gray-600 pt-2">
                        <span className="text-white">Total</span>
                        <span className="text-white">${(selectedOrder.total + calculateTip()).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compact Footer with Pay Button */}
                <div className="px-4 py-2.5 border-t border-gray-600">
                  <button
                    onClick={async () => {
                      console.log('Pay Now clicked');
                      const stripe = await getStripePromise();
                      console.log('Stripe initialized:', !!stripe);
                      if (!stripe) {
                        alert('Payment system is not ready. Please refresh the page and try again.');
                        return;
                      }
                      setShowPaymentModal(true);
                    }}
                    disabled={!checkoutForm.fullName || !checkoutForm.email || !checkoutForm.phone}
                    className="w-full py-2 text-sm text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: (!checkoutForm.fullName || !checkoutForm.email || !checkoutForm.phone) ? '#5a7a92' : 'var(--theme-primary)' 
                    }}
                  >
                    <CreditCard className="h-4 w-4" />
                    Pay Now - ${(selectedOrder.total + calculateTip()).toFixed(2)}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

      {/* Payment Modal - Dark Theme */}
      {showPaymentModal && selectedOrder && (
        <>
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <PaymentModal
                show={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                amount={selectedOrder.total + calculateTip()}
                onPaymentSuccess={handlePaymentSuccess}
                orderData={selectedOrder}
              />
            </Elements>
          ) : (
            ReactDOM.createPortal(
              <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
                <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">Payment System Loading...</h3>
                  <p className="text-sm text-gray-400 mb-4">Please wait while we initialize the payment system.</p>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(41,173,211)]"></div>
                  </div>
                </div>
              </div>,
              document.body
            )
          )}
        </>
      )}

      {/* Payment Confirmation Modal - Dark Theme */}
      {showConfirmationModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-[80] p-4">
          <div className="bg-[#2C3E50] rounded-lg max-w-md w-full shadow-2xl">
            {/* Compact Header */}
            <div className="px-4 py-2.5 border-b border-gray-600 bg-gray-900">
              <h2 className="text-lg font-semibold text-white text-center">Payment Successful</h2>
            </div>

            <div className="p-6 text-center">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-white mb-3">Thank You!</h3>
                <p className="text-sm text-gray-300 mb-2">Your payment has been successfully processed.</p>
                <p className="text-xs text-gray-400 mb-3">Table: {selectedTable?.name}</p>
              </div>

              <div className="bg-gray-700 bg-opacity-30 rounded-lg p-3 mb-4 border border-gray-600">
                <div className="text-xs text-gray-400 mb-1">Amount Paid</div>
                <div className="text-2xl font-bold text-white">
                  ${selectedOrder ? (selectedOrder.total + calculateTip()).toFixed(2) : '0.00'}
                </div>
              </div>

              <div className="bg-gray-700 bg-opacity-20 rounded-lg p-4 mb-4 border border-gray-600">
                <p className="text-sm text-gray-300 leading-relaxed">
                  Thank you for visiting us! We would be delighted to see you again. Please share your experience with us.
                </p>
              </div>

              {/* Review Buttons */}
              <div className="space-y-2">
                <p className="text-xs text-gray-400 mb-2">Review Us On:</p>
                {storeSettings?.google_reviews_link && (
                  <a
                    href={storeSettings.google_reviews_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2 px-4 bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border border-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </a>
                )}
                {storeSettings?.yelp_reviews_link && (
                  <a
                    href={storeSettings.yelp_reviews_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2 px-4 bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border border-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                      <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                    </svg>
                    Yelp
                  </a>
                )}
                {storeSettings?.facebook_reviews_link && (
                  <a
                    href={storeSettings.facebook_reviews_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2 px-4 bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border border-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </a>
                )}
              </div>
            </div>

            {/* Compact Footer */}
            {!isFromBarcodeScan && (
              <div className="px-4 py-2.5 border-t border-gray-600 bg-gray-900">
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-2 text-sm bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border border-gray-600 text-gray-300 font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PayAtTable;
